import { AuditReportData } from '@app/types/auditReport';
import { forwardRef } from 'react';

interface AuditReportPreviewProps {
  data: AuditReportData;
}

export const AuditReportPreview = forwardRef<HTMLDivElement, AuditReportPreviewProps>(({ data }, ref) => {
  const statusLabels: Record<string, string> = {
    datenschutzorganisation: 'Datenschutzorganisation',
    verarbeitungsverzeichnis: 'Verzeichnis von Verarbeitungstätigkeiten',
    rechtsgrundlagen: 'Rechtsgrundlagen der Verarbeitung',
    tom: 'Technische und organisatorische Maßnahmen',
    auftragsverarbeitung: 'Auftragsverarbeitung',
    betroffenenrechte: 'Betroffenenrechte',
    schulungen: 'Schulungen & Sensibilisierung',
  };

  return (
    <div ref={ref} className="audit-report-preview prose max-w-none bg-white p-8 shadow-lg" style={{ fontFamily: 'serif' }}>
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 20mm;
          }
        }
      `}</style>

      {/* Deckseite + Inhaltsverzeichnis */}
      <div className="text-center mb-32 pb-16">
        <h1 className="text-2xl font-bold mb-8">Datenschutz-Auditbericht nach DSGVO und BDSG</h1>
        <p className="text-lg mb-2">für</p>
        <p className="text-xl font-bold mb-8">{data.companyName || '[Name des Unternehmens]'}</p>

        <div className="grid grid-cols-2 gap-12 text-left max-w-4xl mx-auto mb-6">
          <div>
            <p>
              <strong>Geprüfte Organisationseinheit(en):</strong> {data.organizationalUnits || '[...]'}
            </p>
            <p>
              <strong>Sitz der Organisation:</strong> {data.companyAddress || '[Adresse]'}
            </p>
            <p>
              <strong>Prüfzeitraum:</strong> {data.auditPeriod || '[Zeitraum]'}
            </p>
            <p>
              <strong>Datum des Auditberichts:</strong> {data.reportDate || '[Datum]'}
            </p>
          </div>

          <div>
            <p className="font-bold">Datenschutzbeauftragter (DSB):</p>
            <p>Name: {data.dsbName || '[Name]'}</p>
            <p>Art: {data.dsbType === 'intern' ? 'intern' : 'extern'}</p>
            <p>Kontakt: {data.dsbContact || '[E-Mail / Telefon]'}</p>
          </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm italic">
            <strong>Vertraulichkeitshinweis:</strong>
            <br />
            Dieser Bericht enthält vertrauliche Informationen und ist ausschließlich für den internen Gebrauch des
            Auftraggebers bestimmt.
          </p>
        </div>
      </div>

      {/* Inhaltsverzeichnis */}
      <section className="mb-12 mt-16">
        <h2 className="text-xl font-bold mb-4">Inhaltsverzeichnis</h2>
        <ol className="list-decimal list-inside space-y-1">
          <li>Präambel – Allgemeines zum Datenschutz</li>
          <li>Mandanten- und Organisationsinformationen</li>
          <li>Übersicht über den aktuellen Datenschutzstatus</li>
          <li>
            Detaillierte Betrachtung einzelner Themenbereiche
            <ol className="list-decimal list-inside ml-6 mt-1 space-y-0.5">
              <li>Datenschutz-Management-System (DSMS)</li>
              <li>Website und Social Media</li>
              <li>Auftragsverarbeitung</li>
              <li>Technische und organisatorische Maßnahmen (TOM)</li>
              <li>Informationspflichten</li>
              <li>Videoüberwachung</li>
              <li>Mitarbeiter und Beschäftigtendatenschutz</li>
              <li>Verzeichnis von Verarbeitungstätigkeiten und Löschkonzept</li>
              <li>Betroffenenrechte</li>
              <li>Datenschutzverletzungen</li>
              <li>Datenschutz-Folgenabschätzung (DSFA)</li>
              <li>Stellung und Unabhängigkeit des Datenschutzbeauftragten</li>
            </ol>
          </li>
          <li>Vor-Ort-Audit</li>
          <li>Fazit und Gesamteinschätzung</li>
        </ol>
      </section>

      {/* Präambel */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">1. Präambel – Allgemeines zum Datenschutz</h2>
        <p className="mb-3">
          Der Schutz personenbezogener Daten ist ein wesentlicher Bestandteil einer verantwortungsvollen und
          rechtskonformen Unternehmensführung. Mit Inkrafttreten der Datenschutz-Grundverordnung (DSGVO) und des
          Bundesdatenschutzgesetzes (BDSG) wurden die Anforderungen an Unternehmen erheblich konkretisiert und
          verschärft.
        </p>
        <p className="mb-3">
          Ziel des Datenschutzes ist es, die Grundrechte und Grundfreiheiten natürlicher Personen, insbesondere deren
          Recht auf informationelle Selbstbestimmung, zu schützen. Unternehmen sind verpflichtet, personenbezogene Daten
          nur auf einer rechtmäßigen Grundlage, zweckgebunden, transparent und sicher zu verarbeiten.
        </p>
        <p>
          Der vorliegende Datenschutz-Auditbericht dient dazu, den aktuellen Stand der datenschutzrechtlichen Umsetzung
          im Unternehmen darzustellen, bestehende Risiken zu identifizieren und die Einhaltung der gesetzlichen
          Anforderungen zu dokumentieren.
        </p>
      </section>

      {/* Mandanteninformationen */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">2. Mandanten- und Organisationsinformationen</h2>

        <h3 className="text-lg font-bold mt-4 mb-2">2.1 Allgemeine Angaben</h3>
        <ul className="list-none">
          <li>Unternehmensname: {data.companyName || '[Name]'}</li>
          <li>Rechtsform: {data.legalForm || '[z.B. GmbH]'}</li>
          <li>Anschrift: {data.companyAddress || '[Adresse]'}</li>
          <li>Branche: {data.industry || '[Branche]'}</li>
          <li>Mitarbeiterzahl: {data.employeeCount || '[ca. Anzahl]'}</li>
        </ul>

        <h3 className="text-lg font-bold mt-4 mb-2">2.2 Verantwortlicher im Sinne der DSGVO</h3>
        <ul className="list-none">
          <li>Name / Firma: {data.companyName || '[Name]'}</li>
          <li>Vertretungsberechtigte Person(en): {data.responsiblePerson || '[Name]'}</li>
          <li>Kontakt: {data.responsibleContact || '[E-Mail / Telefon]'}</li>
        </ul>

        <h3 className="text-lg font-bold mt-4 mb-2">2.3 Datenschutzbeauftragter</h3>
        <ul className="list-none">
          <li>Name: {data.dsbName || '[Name]'}</li>
          <li>Art: {data.dsbType === 'intern' ? 'intern' : 'extern'}</li>
          <li>Bestelldatum: {data.dsbAppointmentDate || '[Datum]'}</li>
          <li>Kontaktdaten: {data.dsbContact || '[E-Mail / Telefon]'}</li>
        </ul>

        <h3 className="text-lg font-bold mt-4 mb-2">2.4 Zuständige Aufsichtsbehörde</h3>
        <ul className="list-none">
          <li>Name: {data.supervisoryAuthority || '[Name der Aufsichtsbehörde]'}</li>
          <li>Anschrift: {data.supervisoryAuthorityAddress || '[Adresse]'}</li>
          <li>Bundesland: {data.supervisoryAuthorityState || '[Bundesland]'}</li>
        </ul>
      </section>

      {/* Statusübersicht */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">3. Übersicht über den aktuellen Datenschutzstatus</h2>
        <h3 className="text-lg font-bold mt-4 mb-2">3.1 Einleitung</h3>
        <p className="mb-4">
          Im Rahmen dieses Audits wurde der aktuelle Stand der datenschutzrechtlichen Organisation und Umsetzung im
          Unternehmen überprüft. Ziel dieser Übersicht ist es, transparent darzustellen, welche datenschutzrelevanten
          Anforderungen bereits erfüllt sind und in welchen Bereichen noch Handlungsbedarf besteht.
        </p>
        <p className="mb-4">
          Die Bewertung erfolgte auf Basis der vorgelegten Unterlagen, durchgeführter Gespräche sowie ggf.
          stichprobenartiger Prüfungen.
        </p>

        <h3 className="text-lg font-bold mt-4 mb-2">3.2 Statusübersicht</h3>

        <table className="w-full border-collapse border border-gray-300 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2 text-left">Themenbereich</th>
              <th className="border border-gray-300 p-2 text-center">Vorhanden</th>
              <th className="border border-gray-300 p-2 text-center">Teilweise erfüllt</th>
              <th className="border border-gray-300 p-2 text-center">Nicht erfüllt</th>
              <th className="border border-gray-300 p-2 text-left">Anmerkungen</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.statusChecks).map(([key, value]) => (
              <tr key={key}>
                <td className="border border-gray-300 p-2">{statusLabels[key] || key}</td>
                <td className="border border-gray-300 p-2 text-center">{value === 'present' ? '☑' : '☐'}</td>
                <td className="border border-gray-300 p-2 text-center">{value === 'partial' ? '☑' : '☐'}</td>
                <td className="border border-gray-300 p-2 text-center">{value === 'missing' ? '☑' : '☐'}</td>
                <td className="border border-gray-300 p-2 text-sm">{data.statusNotes[key] || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 className="text-lg font-bold mt-6 mb-2">3.3 Zusammenfassende Bewertung des aktuellen Status</h3>
        <p>
          Zusammenfassend lässt sich festhalten, dass im Unternehmen bereits wesentliche datenschutzrechtliche
          Strukturen vorhanden sind. Gleichzeitig bestehen in einzelnen Bereichen noch Optimierungs- und
          Umsetzungsbedarfe, die zur vollständigen Erfüllung der gesetzlichen Anforderungen adressiert werden sollten.
        </p>
      </section>

      {/* Detaillierte Betrachtung */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">4. Detaillierte Betrachtung einzelner Themenbereiche</h2>

        <h3 className="text-lg font-bold mt-4 mb-2">4.1 Datenschutz-Management-System (DSMS)</h3>
        <p className="mb-2">
          Es wurde geprüft, ob ein systematisches Datenschutz-Management-System implementiert ist, das die fortlaufende
          Einhaltung der datenschutzrechtlichen Anforderungen sicherstellt.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Festgelegte Datenschutzrichtlinien und -prozesse</li>
          <li>Regelmäßige Überprüfung und Aktualisierung</li>
          <li>Dokumentation und Nachvollziehbarkeit</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.dsms || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.2 Website und Social Media</h3>
        <p className="mb-2">
          Gegenstand der Prüfung war die datenschutzrechtliche Ausgestaltung der Unternehmenswebsite sowie der
          eingesetzten Social-Media-Kanäle.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Datenschutzerklärung</li>
          <li>Cookie- und Consent-Management</li>
          <li>Tracking- und Analysetools</li>
          <li>Social-Media-Präsenzen und Einbindungen</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.website || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.3 Auftragsverarbeitung</h3>
        <p className="mb-2">
          Es wurde geprüft, ob Auftragsverarbeitungen im Sinne von Art. 28 DSGVO ordnungsgemäß geregelt sind.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Vorliegen von Auftragsverarbeitungsverträgen</li>
          <li>Inhaltliche Vollständigkeit</li>
          <li>Kontrolle und Auswahl der Dienstleister</li>
          <li>Drittlandübermittlungen</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.auftragsverarbeitung || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.4 Technische und organisatorische Maßnahmen (TOM)</h3>
        <p className="mb-2">
          Die technischen und organisatorischen Maßnahmen nach Art. 32 DSGVO wurden hinsichtlich Angemessenheit und
          Umsetzung überprüft.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Zutritts-, Zugangs- und Zugriffskontrollen</li>
          <li>Weitergabekontrolle</li>
          <li>Integrität und Verfügbarkeit</li>
          <li>Datensicherung und Notfallmanagement</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.tom || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.5 Informationspflichten</h3>
        <p className="mb-2">
          Es wurde geprüft, ob Informationspflichten nach Art. 12–14 DSGVO ordnungsgemäß erfüllt werden.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Inhalte und Transparenz</li>
          <li>Zeitpunkt der Information</li>
          <li>Verständlichkeit für Betroffene</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.informationspflichten || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.6 Videoüberwachung</h3>
        <p className="mb-2">
          Sofern im Unternehmen Videoüberwachung eingesetzt wird, wurde diese gesondert betrachtet.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Zweck der Videoüberwachung</li>
          <li>Rechtsgrundlage</li>
          <li>Kennzeichnung der überwachten Bereiche</li>
          <li>Speicherfristen</li>
          <li>Zugriffsberechtigungen</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.videoueberwachung || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.7 Mitarbeiter und Beschäftigtendatenschutz</h3>
        <p className="mb-2">Es wurde geprüft, wie personenbezogene Daten von Beschäftigten verarbeitet werden.</p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Rechtsgrundlagen nach § 26 BDSG</li>
          <li>Bewerbungsverfahren</li>
          <li>Personalakten</li>
          <li>Schulungen und Sensibilisierung</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.mitarbeiter || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.8 Verzeichnis von Verarbeitungstätigkeiten und Löschkonzept</h3>
        <p className="mb-2">
          Gegenstand der Prüfung war das Verzeichnis von Verarbeitungstätigkeiten gemäß Art. 30 DSGVO sowie das zugrunde
          liegende Lösch- und Datenschutzkonzept.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Vollständigkeit und Aktualität</li>
          <li>Struktur und Nachvollziehbarkeit</li>
          <li>Regelungen zu Speicher- und Löschfristen</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.verzeichnis || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.9 Betroffenenrechte</h3>
        <p className="mb-2">
          Es wurde geprüft, ob Prozesse zur Wahrnehmung der Betroffenenrechte nach Art. 12–22 DSGVO bestehen.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Zuständigkeiten</li>
          <li>Fristenkontrolle</li>
          <li>Dokumentation der Anfragen</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.betroffenenrechte || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.10 Datenschutzverletzungen</h3>
        <p className="mb-2">
          Im Rahmen des Audits wurde geprüft, ob Prozesse zur Erkennung, Bewertung, Dokumentation und Meldung von
          Datenschutzverletzungen bestehen.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Interne Meldewege</li>
          <li>Risikobewertung</li>
          <li>Meldung an die Aufsichtsbehörde (Art. 33 DSGVO)</li>
          <li>Benachrichtigung Betroffener (Art. 34 DSGVO)</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.datenschutzverletzungen || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.11 Datenschutz-Folgenabschätzung (DSFA)</h3>
        <p className="mb-2">
          Es wurde überprüft, ob Verarbeitungstätigkeiten vorliegen, die eine Datenschutz-Folgenabschätzung erfordern.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Identifikationsverfahren</li>
          <li>Durchführung und Dokumentation</li>
          <li>Einbindung des Datenschutzbeauftragten</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.dsfa || '[Bewertung ausstehend]'}</p>

        <h3 className="text-lg font-bold mt-6 mb-2">4.12 Stellung und Unabhängigkeit des Datenschutzbeauftragten</h3>
        <p className="mb-2">
          Es wurde geprüft, ob die gesetzlichen Anforderungen an die Stellung des Datenschutzbeauftragten erfüllt sind.
        </p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Weisungsfreiheit (Art. 38 DSGVO)</li>
          <li>Keine Interessenkonflikte</li>
          <li>Ausreichende personelle und zeitliche Ressourcen</li>
          <li>Direkter Zugang zur Geschäftsleitung</li>
        </ul>
        <p className="whitespace-pre-wrap">{data.sections.dsbPosition || '[Bewertung ausstehend]'}</p>
      </section>

      {/* Vor-Ort-Audit */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">5. Vor-Ort-Audit</h2>
        <p className="mb-2">Sofern ein Vor-Ort-Audit durchgeführt wurde, umfasste dieses insbesondere:</p>
        <ul className="list-disc list-inside ml-4 mb-4">
          <li>Begehung relevanter Bereiche</li>
          <li>Prüfung physischer Zutrittskontrollen</li>
          <li>Sichtprüfung von IT-Arbeitsplätzen</li>
          <li>Gespräche mit Mitarbeitenden</li>
        </ul>
        <p>Die Ergebnisse des Vor-Ort-Audits flossen in die Gesamtbewertung ein.</p>
      </section>

      {/* Fazit */}
      
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">6. Fazit und Gesamteinschätzung</h2>
        <p className="whitespace-pre-wrap">
          {data.fazit ||
            'Der vorliegende Datenschutz-AuditReport zeigt den aktuellen Stand der datenschutzrechtlichen Umsetzung im Unternehmen auf. Insgesamt ist festzustellen, dass grundlegende Strukturen vorhanden sind, jedoch in einzelnen Bereichen weiterer Handlungsbedarf besteht.\n\nDurch die konsequente Umsetzung der identifizierten Maßnahmen kann das Datenschutzniveau weiter verbessert und rechtliche sowie wirtschaftliche Risiken nachhaltig reduziert werden.'}
        </p>
      </section>
    </div>
  );
});

AuditReportPreview.displayName = 'AuditReportPreview';
